import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import PhoneInput from 'react-phone-number-input/input';

import ReactCountrySelect from 'Components/ReactCountrySelect';

// Redux actions
import { createOrganization, resetOnboarding } from 'Redux/actions/onboarding';
import { apiPatchUserInformation } from 'Redux/actions/user_information';

// Shared page styles
import {
  Container,
  Footer,
  FooterButton,
  Form,
  Heading,
  MainContainer,
  SubHeader,
  TextLink,
} from '../styles';

// Images
import CompanyLogo from '../../../Assets/images/companyLogo.svg';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY_CONTACT = {
  email: '',
  phone: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  postal_code: '',
  country: '',
};

const Onboarding = ({ handleStep, handleModal }) => {
  const [orgName, setOrgName] = useState('');
  const [showContactStep, setShowContactStep] = useState(false);
  const [contact, setContact] = useState(EMPTY_CONTACT);

  const dispatch = useDispatch();
  const history = useHistory();
  const onboarding = useSelector(state => state.onboarding);
  const auth = useSelector(state => state.auth);

  const userInformation = auth?.user_information;

  useEffect(() => {
    if (!userInformation) return;
    setContact(current => ({
      ...current,
      email: current.email || userInformation.email || '',
    }));
  }, [userInformation]);

  const handleContactChange = e =>
    setContact({ ...contact, [e.target.name]: e.target.value });

  // What a courier needs to ship hardware; mirrors the backend rules.
  const isContactValid =
    contact.address_line1.trim().length > 0 &&
    contact.city.trim().length > 0 &&
    contact.postal_code.trim().length > 0 &&
    contact.country.length > 0 &&
    isPossiblePhoneNumber(contact.phone) &&
    EMAIL_PATTERN.test(contact.email.trim());

  const handleCreateOrganisation = () => {
    dispatch(createOrganization({ name: orgName, contact }, auth.token));
  };

  useEffect(() => {
    if (onboarding.orgSuccess) {
      history.push('/settings/assets');
      dispatch(resetOnboarding());
      dispatch(
        apiPatchUserInformation(
          { first_login: false },
          auth.user_information?.id,
          auth.token,
          null,
          false,
        ),
      );
    }
  }, [onboarding.orgData]);

  return (
    <MainContainer>
      <Container>
        <img src={CompanyLogo} alt="Company Logo" />
        <Heading>Welcome to AIR Cloud</Heading>
        {!showContactStep ? (
          <>
            <SubHeader>
              To get started with your new account, please enter your
              organization name below and click continue.
            </SubHeader>
            <Form onSubmit={e => e.preventDefault()}>
              <div className="d-grid gap-3">
                <input
                  type="text"
                  className="form-control"
                  id="organization"
                  placeholder="Enter Organization Name*"
                  maxLength={150}
                  onChange={e => setOrgName(e.target.value)}
                  value={orgName}
                />
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={orgName.trim().length === 0}
                  onClick={() => setShowContactStep(true)}
                >
                  Continue
                </button>
              </div>
            </Form>
          </>
        ) : (
          <>
            <SubHeader>
              Tell us how to reach your organization.
            </SubHeader>
            <Form onSubmit={e => e.preventDefault()}>
              <div className="d-grid gap-3">
                {/* Fields scroll on short viewports; the buttons stay put. */}
                <div
                  className="d-grid gap-3"
                  style={{
                    maxHeight: '30vh',
                    overflowY: 'auto',
                    // Room for the input focus ring, which overflow would clip.
                    padding: '0.25rem',
                  }}
                >
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    placeholder="Organization Email*"
                    maxLength={254}
                    onChange={handleContactChange}
                    value={contact.email}
                  />
                  <input
                    type="text"
                    className="form-control"
                    name="address_line1"
                    placeholder="Address Line 1*"
                    maxLength={255}
                    onChange={handleContactChange}
                    value={contact.address_line1}
                  />
                  <input
                    type="text"
                    className="form-control"
                    name="address_line2"
                    placeholder="Address Line 2 (optional)"
                    maxLength={255}
                    onChange={handleContactChange}
                    value={contact.address_line2}
                  />
                  <input
                    type="text"
                    className="form-control"
                    name="city"
                    placeholder="City*"
                    maxLength={100}
                    onChange={handleContactChange}
                    value={contact.city}
                  />
                  <input
                    type="text"
                    className="form-control"
                    name="state"
                    placeholder="State / Province (optional)"
                    maxLength={100}
                    onChange={handleContactChange}
                    value={contact.state}
                  />
                  <input
                    type="text"
                    className="form-control"
                    name="postal_code"
                    placeholder="Postal Code*"
                    maxLength={20}
                    onChange={handleContactChange}
                    value={contact.postal_code}
                  />
                  {/* Portal: the scrolling field list would clip the menu. */}
                  <ReactCountrySelect
                    value={contact.country}
                    onChange={option =>
                      setContact(current => ({
                        ...current,
                        country: option?.value ?? '',
                      }))
                    }
                    menuPortalTarget={document.body}
                    placeholder="Country*"
                  />
                  {/* After country: it sets the default dialing code, so a
                      local number works; stored as E.164 like org settings. */}
                  <PhoneInput
                    className="form-control"
                    placeholder="Organization Phone*"
                    defaultCountry={contact.country || undefined}
                    value={contact.phone || undefined}
                    onChange={value =>
                      setContact(current => ({ ...current, phone: value ?? '' }))
                    }
                  />
                </div>
                {/* Back sits beside Confirm, secondary action on the left. */}
                <div className="row g-3">
                  <div className="col-6 d-grid">
                    <button
                      type="button"
                      className="btn btn-light btn-sm"
                      onClick={() => setShowContactStep(false)}
                    >
                      Back
                    </button>
                  </div>
                  <div className="col-6 d-grid">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={!isContactValid}
                      onClick={handleCreateOrganisation}
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              </div>
            </Form>
          </>
        )}
        <SubHeader className="form-text text-center">
          <TextLink to="" onClick={handleStep}>
            Skip this step
          </TextLink>
        </SubHeader>
      </Container>
      <Footer>
        <FooterButton type="button" className="btn" onClick={handleModal}>
          Launch Tutorial
        </FooterButton>
      </Footer>
    </MainContainer>
  );
};

export default Onboarding;
